import { faCheck, faDownload, faFile } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@ui';
import { get } from 'lodash';
import moment from 'moment';
import { Fragment, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { useDownload } from '../../hooks';
import { useApi } from '../../hooks/useApi';
import { useClubSlug } from '../Clubs/hooks';
import Loader from '../Loader';

const flattenFields = (fields = []) =>
    fields.reduce((carry, field) => {
        if (field.type === 'section') {
            return [...carry, ...(field.children || []).flatMap((child) => flattenFields(child.fields || []))];
        }
        return [...carry, field];
    }, []);

const formatPrice = (price) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(price);

const flattenPricingItems = (items = []) =>
    items.flatMap((item) => [item, ...flattenPricingItems(item.children || [])]);

const getPricingSelectionId = (selection) => {
    if (typeof selection === 'string' || typeof selection === 'number') return String(selection);
    if (selection && typeof selection === 'object' && selection.id != null) return String(selection.id);
    return '';
};

const getPricingSelectionQuantity = (selection) => {
    if (selection && typeof selection === 'object' && selection.quantity != null) return selection.quantity;
    return null;
};

const truncateLabel = (label) => (label?.length > 100 ? label.substr(0, 100) + ' …' : label);

/**
 * Letters and digits only, with both spellings of a German umlaut collapsed
 * onto one.
 *
 * "regelmäßig" is slugged as "regelmassig" by one generator and would fold to
 * "regelmaig" by simply stripping the accents, so ß and ss meet at s, and ü and
 * ue at u — otherwise half the matches fail on exactly the words German forms
 * are full of.
 */
const looseKey = (value) =>
    String(value ?? '')
        .toLowerCase()
        .replace(/ß/g, 's')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/(ae|oe|ue)/g, (match) => match[0])
        .replace(/ss/g, 's')
        .replace(/[^a-z0-9]/g, '');

/**
 * The option as it was written on the form, where the stored value is a slug of it.
 *
 * Forms store a chosen option sometimes as its label and sometimes as a slugged
 * version, depending on when the form was built. Real submissions come back
 * holding "powerkurs-24-x-60-minuten" or "ich-bin-mannschaftsspieler-in" — the
 * right answer, spelled in a way nobody reads. The field carries its own
 * options, so the label is recoverable rather than guessable.
 *
 * Compared on letters and digits alone, because the separators are the part
 * that cannot be relied on: whatever produced these slugs splits "24x" into
 * "24-x" and "*in" into "-in", which no single slug function reproduces.
 * Anything that does not match an option is returned untouched.
 */
const resolveOptionLabel = (field) => {
    const value = field?.value;

    if (typeof value !== 'string' || !Array.isArray(field?.options)) return value;

    const wanted = looseKey(value);
    const match = field.options.find((option) => {
        const label = typeof option === 'object' ? (option?.label ?? option?.option ?? option?.value) : option;
        return label && looseKey(label) === wanted;
    });

    if (!match) return value;

    return typeof match === 'object' ? (match.label ?? match.option ?? match.value) : match;
};

const CategoriesPreview = ({ categories }: { categories: { id: number; title: string; color: string }[] }) => {
    if (!categories?.length) return <span className="tw-text-accents-4">—</span>;
    return (
        <div className="tw-flex tw-flex-wrap tw-gap-1.5">
            {categories.map((cat) => (
                <span
                    key={cat.id}
                    className="tw-inline-flex tw-items-center tw-rounded-full tw-px-3 tw-py-0.5 tw-text-xs tw-font-medium tw-text-white"
                    style={{ backgroundColor: cat.color }}
                >
                    {cat.title}
                </span>
            ))}
        </div>
    );
};

const PricingTablePreview = ({ field }) => {
    const clubSlug = useClubSlug();
    const { data, isLoading } = useSWR(
        field.formItemGroupId && clubSlug ? `club/${clubSlug}/form-item-group/${field.formItemGroupId}/form-item` : null
    );

    if (isLoading) return <span className="tw-text-accents-5">…</span>;

    const items = flattenPricingItems(data?.data ?? []);
    let value = field.value;

    if (typeof value === 'string' && value.startsWith('[')) {
        try {
            value = JSON.parse(value);
        } catch (e) {
            console.log('Failed to parse pricing table value:', e);
        }
    }

    if (!value || (Array.isArray(value) && value.length === 0)) return <span className="tw-text-accents-4">—</span>;

    const selections = Array.isArray(value) ? value : [value];

    return (
        <ul className="tw-m-0 tw-space-y-1 tw-pl-4">
            {selections.map((sel, i) => {
                const id = getPricingSelectionId(sel);
                const qty = getPricingSelectionQuantity(sel);
                const item = items.find((it) => String(it.id) === String(id));

                if (!item)
                    return (
                        <li key={i} className="tw-text-sm tw-text-accents-8">
                            {id ? `Item ID: ${id}` : 'Unbekanntes Element'}
                        </li>
                    );

                return (
                    <li key={i} className="tw-text-sm tw-text-accents-8">
                        {item.title}
                        {qty && qty > 1 && ` × ${qty}`}
                        {item.unit && ` ${item.unit}`}
                        <span className="tw-ml-1 tw-text-accents-5">({formatPrice(item.price)})</span>
                    </li>
                );
            })}
        </ul>
    );
};

const SignaturePreview = ({ field }) => {
    if (!field.value || typeof field.value !== 'string') return <span className="tw-text-accents-4">—</span>;

    const handleDownload = () => {
        const blob = new Blob([field.value], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${field.label || 'Unterschrift'}.svg`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="tw-space-y-2">
            <div
                className="tw-rounded-lg tw-border tw-border-solid tw-border-accents-2 tw-bg-white tw-p-2"
                dangerouslySetInnerHTML={{ __html: field.value }}
            />
            <button
                type="button"
                onClick={handleDownload}
                className="tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-1.5 tw-border-0 tw-bg-transparent tw-p-0 tw-text-xs tw-text-success hover:tw-text-success/70"
            >
                <FontAwesomeIcon icon={faDownload} className="tw-text-[10px]" />
                SVG herunterladen
            </button>
        </div>
    );
};

const FilePreview = ({ field }) => {
    const fileId = get(field, 'value.id') || field.value;

    const { data, isLoading } = useSWR(fileId ? `file/view/${fileId}` : null);
    if (isLoading) return <span className="tw-text-accents-5">…</span>;
    const file = data?.data;
    if (!file) return <span className="tw-text-accents-4">—</span>;

    return (
        <a
            href={file.url}
            target="_blank"
            rel="noopener noreferrer"
            className="tw-inline-flex tw-w-full tw-items-center tw-gap-3 tw-rounded-xl tw-border tw-border-solid tw-border-accents-3 tw-bg-white tw-py-2.5 tw-pl-2.5 tw-pr-4 tw-text-sm tw-text-accents-8 tw-no-underline tw-transition-colors hover:tw-border-success/40 hover:tw-bg-success/5 hover:tw-text-success"
        >
            <span className="tw-flex tw-size-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-bg-success/10 tw-text-success">
                <FontAwesomeIcon icon={faFile} />
            </span>
            <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-font-medium">{file.name}</span>
            <FontAwesomeIcon icon={faDownload} className="tw-shrink-0 tw-text-xs tw-text-accents-5" />
        </a>
    );
};

const FormDataPreview = ({ formDataId, ...props }) => {
    const { t } = useTranslation('common');
    const api = useApi();
    const download = useDownload();
    const { data, isLoading } = useSWR('form-data/' + formDataId, null, {
        revalidateOnFocus: false,
        revalidateIfStale: false
    });
    const formData = data?.data;

    const handleDownloadPdf = useCallback(() => {
        void api({
            url: `form-data/${formDataId}/download`,
            method: 'GET',
            responseType: 'blob',
            onSuccess: (blob) => download(blob, 'Formulardaten.pdf')
        });
    }, [formDataId, download]);

    const renderFieldValue = useCallback((field) => {
        if (field.type === 'checkbox') {
            if (Array.isArray(field.value)) return field.value.join(', ');
            if (typeof field.value === 'string') return field.value;
            return field.value ? 'Ja' : 'Nein';
        }

        if (field.type === 'date') {
            const parsed = moment(field.value);
            return parsed.isValid() ? parsed.format('DD.MM.YYYY') : '—';
        }

        if (field.type === 'address') {
            const v = field.value;
            if (!v || typeof v !== 'object') return '—';
            const line1 = [v.street, v.streetnumber].filter(Boolean).join(' ');
            const line2 = [v.postalcode, v.city].filter(Boolean).join(' ');
            return [line1, line2].filter(Boolean).join(', ') || '—';
        }

        if (field.type === 'payment_options') {
            const selectedOption = field.options?.find((option) => String(option.price) === String(field.value));
            return selectedOption ? `${selectedOption.option} (${selectedOption.price} €)` : field.value;
        }

        if (typeof field.value === 'object') return field.value?.label;

        return resolveOptionLabel(field);
    }, []);

    return (
        <Loader isLoading={isLoading}>
            <Button block size="xs" onPress={handleDownloadPdf} className="tw-mb-4">
                {t('formDataPreview.downloadPdf')}
            </Button>
            <div
                {...props}
                className={'tw-grid tw-px-2' + (props.className ?? '')}
                style={{ gridTemplateColumns: 'minmax(auto,30%) 1fr' }}
            >
                {(() => {
                    const visibleFields = flattenFields(formData || [])
                        .filter((f) => !['wysiwyg', 'section'].includes(f.type))
                        .sort((a, b) => (a.type === 'categories' ? -1 : b.type === 'categories' ? 1 : 0));
                    return visibleFields.map((field, index) => {
                        const isLast = index === visibleFields.length - 1;
                        const rowBorder = isLast ? '' : 'tw-border-0 tw-border-b tw-border-solid tw-border-accents-2';
                        return (
                            <Fragment key={index}>
                                <span
                                    className={`tw-line-clamp-4 tw-py-3 tw-pr-6 tw-text-sm tw-font-semibold tw-tracking-wide tw-text-accents-7 ${rowBorder}`}
                                >
                                    {truncateLabel(field.label)}
                                </span>
                                <div className={`tw-min-w-0 tw-py-3 tw-text-sm tw-text-accents-9 ${rowBorder}`}>
                                    {field.type === 'categories' ? (
                                        <CategoriesPreview categories={field.value} />
                                    ) : field.type === 'signature' ? (
                                        <SignaturePreview field={field} />
                                    ) : field.type === 'file' ? (
                                        <FilePreview field={field} />
                                    ) : field.type === 'pricing_table' ? (
                                        <PricingTablePreview field={field} />
                                    ) : field.type === 'table' ? (
                                        <div className="tw-overflow-x-auto tw-rounded-lg tw-border tw-border-solid tw-border-accents-2">
                                            <table className="tw-w-full tw-text-sm">
                                                <thead>
                                                    <tr className="tw-bg-accents-0">
                                                        <th
                                                            className="tw-border-0 tw-border-b tw-border-solid tw-border-accents-2 tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-accents-6"
                                                            scope="col"
                                                        />
                                                        {field.columns.map((col) => (
                                                            <th
                                                                key={col}
                                                                scope="col"
                                                                className="tw-border-0 tw-border-b tw-border-solid tw-border-accents-2 tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-accents-6"
                                                            >
                                                                {col}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {field.rows.map((row, ri) => (
                                                        <tr key={ri}>
                                                            <th
                                                                scope="row"
                                                                className={[
                                                                    'tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-accents-7',
                                                                    ri < field.rows.length - 1 &&
                                                                        'tw-border-0 tw-border-b tw-border-solid tw-border-accents-2'
                                                                ]
                                                                    .filter(Boolean)
                                                                    .join(' ')}
                                                            >
                                                                {row}
                                                            </th>
                                                            {field.columns.map((_, ci) => (
                                                                <td
                                                                    key={ci}
                                                                    className={[
                                                                        'tw-px-3 tw-py-2 tw-text-center',
                                                                        ri < field.rows.length - 1 &&
                                                                            'tw-border-0 tw-border-b tw-border-solid tw-border-accents-2'
                                                                    ]
                                                                        .filter(Boolean)
                                                                        .join(' ')}
                                                                >
                                                                    {get(field.value, `${ri}.${ci}`) ? (
                                                                        <FontAwesomeIcon
                                                                            icon={faCheck}
                                                                            className="tw-text-success"
                                                                        />
                                                                    ) : (
                                                                        ''
                                                                    )}
                                                                </td>
                                                            ))}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <span>
                                            {renderFieldValue(field) || <span className="tw-text-accents-6">—</span>}
                                        </span>
                                    )}
                                </div>
                            </Fragment>
                        );
                    });
                })()}
            </div>
        </Loader>
    );
};

export default FormDataPreview;
